<template>
  <div class="all-customers">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-office-building"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('nav.customer_settings') }}</h2>
          <p class="section-header__subtitle">{{ getCustomer.length }} {{ $t('settings.entries') }}</p>
        </div>
      </div>
      <div class="section-header__actions">
        <el-button
          class="btn-add"
          type="primary"
          icon="el-icon-plus"
          @click="$router.push({path: `/app/settings/customer/create`})">
          {{ $t('settings.customerCreate') }}
        </el-button>
        <base-refresh-button
          :loading="loading"
          @click="refreshCustomers"/>
      </div>
    </div>
    <div class="table-card">
      <base-table
        :data="getCustomer"
        highlight-current-row
        class="connections-table"
        style="width: 100%"
        @current-change="onRowSelected">

        <el-table-column
          prop="name"
          width="160"
          :label="$t('settings.name')">
          <template slot-scope="scope">
            <span class="cell-bold">{{ scope.row.name }}</span>
          </template>
        </el-table-column>

        <el-table-column min-width="200" :label="$t('settings.lastSynchronized')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.lastSynchronized">
              <i class="el-icon-time cell-url__icon"></i>
              <span>{{ scope.row.lastSynchronized | moment("DD.MM.YYYY · HH:mm") }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="200" :label="$t('settings.lastSynchronizedTry')">
          <template slot-scope="scope">
            <div
              v-if="scope.row.lastSynchronizeTry && scope.row.lastSynchronized"
              class="sync-status"
              :class="wasLastSynchronizeTrySuccessfully(scope.row) ? 'sync-status--ok' : 'sync-status--err'">
              <i
                :class="wasLastSynchronizeTrySuccessfully(scope.row) ? 'el-icon-circle-check' : 'el-icon-circle-close'"></i>
              {{ scope.row.lastSynchronizeTry | moment("DD.MM.YYYY · HH:mm") }}
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column min-width="200" :label="$t('settings.nextSynchronizeTry')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.nextSynchronizeTry">
              <i class="el-icon-alarm-clock cell-url__icon"></i>
              <span>{{ scope.row.nextSynchronizeTry | moment("DD.MM.YYYY · HH:mm") }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="userCount" width="130" align="center" :label="$t('settings.userCount')">
          <template slot-scope="scope">
            <el-tag type="info" size="mini" class="domain-tag">
              <i class="el-icon-user"></i> {{ scope.row.userCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="localUserCount" width="110" align="center" :label="$t('settings.localUserCount')">
          <template slot-scope="scope">
            <el-tag size="mini" class="domain-tag">{{ scope.row.localUserCount || 0 }}</el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="usersWithTokenCount" width="120" align="center"
                         :label="$t('settings.usersWithTokenCount')">
          <template slot-scope="scope">
            <el-tag type="success" size="mini" class="domain-tag">
              <i class="el-icon-mobile-phone"></i> {{ scope.row.usersWithTokenCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          align="center"
          :label="$t('settings.operations')"
          width="120">
          <template slot-scope="customer">
            <div class="action-cell">
              <el-tooltip :content="$t('settings.edit')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  class="btn-delete"
                  @click.stop.prevent="onCustomerEdit(customer.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.syncCustomerAccounts')" placement="top">
                <el-button
                  :disabled="!customer.row.remoteAccountProviderCount || customer.row.remoteAccountProviderCount <= 0"
                  type="primary"
                  icon="el-icon-refresh"
                  size="mini"
                  circle
                  class="btn-delete"
                  @click.stop.prevent="onCustomerSync(customer.row)"
                  :loading="customer.row.loading">
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </base-table>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {customerService} from "@/services/CustomerService.class";
import BaseTable from '@/components/common/BaseTable';
import BaseRefreshButton from '@/components/common/BaseRefreshButton';
import moment from 'moment';

const mspStateMappers = createNamespacedHelpers('msp');

export default {
  name: "AllCustomers",
  components: {BaseTable, BaseRefreshButton},
  data() {
    return {
      loading: false
    };
  },
  computed: {
    ...mspStateMappers.mapGetters(['getCustomer'])
  },
  mounted() {
    this.refreshCustomers()
  },
  methods: {
    ...mspStateMappers.mapActions(['getCustomersAction']),
    refreshCustomers() {
      this.loading = true;
      Promise.resolve(this.getCustomersAction())
        .finally(() => {
          this.loading = false;
        });
    },
    onCustomerEdit(customer) {
      this.$router.push({path: `/app/settings/${customer.id}/edit`})
    },
    onCustomerSync(customer) {
      customer.loading = true;
      customerService.syncAccounts(customer.id)
        .then(() => {
          this.$message({
            message: `${customer.name} ${this.$t('settings.showCustomerAccountsSuccess')}`,
            type: 'success'
          });
        }).catch(() => {
        this.$message({message: `${customer.name} ${this.$t('settings.showCustomerAccountsError')}`, type: 'error'});
      }).finally(() => {
        this.getCustomersAction();
        customer.loading = false;
      });
    },
    onRowSelected(customer) {
      this.$router.push({path: `/app/settings/${customer.id}/accounts`})
    },
    wasLastSynchronizeTrySuccessfully(customer) {
      const lastSynchronized = moment(customer.lastSynchronized);
      const lastSynchronizeTry = moment(customer.lastSynchronizeTry);
      return lastSynchronized >= lastSynchronizeTry;
    }
  }
}
</script>

<style lang="scss">
.all-customers .connections-table .el-table__body-wrapper tr {
  cursor: pointer;

  &.current-row td {
    background: #ecf5ff !important;
  }
}
</style>

<style scoped lang="scss">
$primary: #409EFF;
$border: #e8e8ee;
$text-primary: #1a1a2e;
$text-secondary: #606266;

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, #409EFF 0%, #2d7dd2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: $text-primary;
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: $text-secondary;
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.btn-add {
  border-radius: 8px;
  font-weight: 500;
  padding: 9px 18px;
  height: auto;
  letter-spacing: 0.01em;
}

.table-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid $border;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.cell-bold {
  font-weight: 600;
  font-size: 13.5px;
  color: $text-primary;
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 8px;
  color: $text-secondary;
  font-size: 13px;

  &__icon {
    color: #bbb;
    font-size: 14px;
    flex-shrink: 0;
  }
}

.cell-empty {
  color: #ccc;
}

.sync-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;

  &--ok {
    color: #67C23A;
  }

  &--err {
    color: #F56C6C;
  }

  i {
    font-size: 15px;
  }
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-delete {
  border-radius: 7px;
}
</style>
